import React, { useEffect, useState } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  UsersIcon,
  CheckCircleIcon,
  BanknotesIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  PauseIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import {
  useCustomersPage,
  useCustomerSummary,
  useCustomerCreditInfo,
  useCreateCustomer,
  useUpdateCustomer,
  useDeleteCustomer,
} from '../../hooks/useCustomers';
import {
  CreateCustomerRequest,
  CustomerListItem,
  CustomerSortField,
} from '../../types/customer.types';
import CustomerForm from '../../components/forms/CustomerForm';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import {
  ConfirmDialog,
  DetailCard,
  DetailItem,
  FormActions,
  badgeClass,
  formatMoney,
  formatNumber,
  getApiErrorMessage,
  primaryButtonClass,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import { defaultRowsPerPage } from '../../lib/preferences';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const SORT_OPTIONS: { value: string; label: string; sortBy: CustomerSortField; sortOrder: 'ASC' | 'DESC' }[] = [
  { value: 'name', label: 'Name A–Z', sortBy: 'name', sortOrder: 'ASC' },
  { value: 'code', label: 'Code A–Z', sortBy: 'code', sortOrder: 'ASC' },
  { value: 'creditLimit', label: 'Highest credit limit', sortBy: 'creditLimit', sortOrder: 'DESC' },
  { value: 'createdAt', label: 'Newest first', sortBy: 'createdAt', sortOrder: 'DESC' },
];

const ORDER_STATUS_LABELS: Record<string, string> = {
  order: 'Order',
  confirmed: 'Confirmed',
  shipped: 'Shipped',
  delivered: 'Delivered',
};

const formatDate = (value?: string | null) =>
  value
    ? new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

// Share of the credit limit in use; null when the customer has no limit
const getUtilisation = (customer: { creditLimit: number; currentOutstanding: number }) =>
  customer.creditLimit > 0 ? (customer.currentOutstanding / customer.creditLimit) * 100 : null;

const getUtilisationTone = (percent: number) => {
  if (percent > 100) return { bar: 'bg-red-500', text: 'text-red-700' };
  if (percent >= 80) return { bar: 'bg-yellow-500', text: 'text-yellow-700' };
  return { bar: 'bg-indigo-500', text: 'text-gray-500' };
};

const CustomerList: React.FC = () => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | ''>('');
  const [creditFilter, setCreditFilter] = useState<'over_limit' | ''>('');
  const [sort, setSort] = useState(SORT_OPTIONS[0].value);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultRowsPerPage);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<CustomerListItem | null>(null);
  const [viewCustomer, setViewCustomer] = useState<CustomerListItem | null>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CustomerListItem | null>(null);
  const [toggleTarget, setToggleTarget] = useState<CustomerListItem | null>(null);

  // Debounce search so the API is not called on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const sortOption = SORT_OPTIONS.find((option) => option.value === sort) || SORT_OPTIONS[0];
  const { data: customerPage, isLoading, isFetching, error } = useCustomersPage({
    page,
    limit: pageSize,
    search: debouncedSearch || undefined,
    isActive: statusFilter ? statusFilter === 'active' : undefined,
    credit: creditFilter || undefined,
    sortBy: sortOption.sortBy,
    sortOrder: sortOption.sortOrder,
  });
  const { data: summary, isLoading: isSummaryLoading } = useCustomerSummary();
  const { data: creditInfo, isLoading: isCreditLoading } = useCustomerCreditInfo(
    isViewOpen && viewCustomer ? viewCustomer.id : null,
  );
  const createMutation = useCreateCustomer();
  const updateMutation = useUpdateCustomer();
  const deleteMutation = useDeleteCustomer();

  const customers = customerPage?.data ?? [];
  const total = customerPage?.total ?? 0;
  const totalPages = customerPage?.totalPages ?? 1;
  const isSaving = createMutation.isPending || (updateMutation.isPending && isFormOpen);

  // Step back when the current page no longer exists
  useEffect(() => {
    if (customerPage && page > 1 && page > customerPage.totalPages) {
      setPage(Math.max(1, customerPage.totalPages));
    }
  }, [customerPage, page]);

  const openCreate = () => {
    setEditingCustomer(null);
    setIsFormOpen(true);
  };

  const openEdit = (customer: CustomerListItem) => {
    setEditingCustomer(customer);
    setIsFormOpen(true);
  };

  const openView = (customer: CustomerListItem) => {
    setViewCustomer(customer);
    setIsViewOpen(true);
  };

  const handleSubmit = async (data: CreateCustomerRequest) => {
    try {
      if (editingCustomer) {
        await updateMutation.mutateAsync({ id: editingCustomer.id, data });
        toast.success('Customer updated successfully');
      } else {
        await createMutation.mutateAsync(data);
        toast.success('Customer created successfully');
        setPage(1);
      }
      setIsFormOpen(false);
    } catch (err) {
      toast.error(
        getApiErrorMessage(err, editingCustomer ? 'Could not update the customer' : 'Could not create the customer'),
      );
      throw err;
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast.success('Customer deleted successfully');
      setDeleteTarget(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not delete the customer'));
    }
  };

  const confirmToggle = async () => {
    if (!toggleTarget) return;
    try {
      await updateMutation.mutateAsync({ id: toggleTarget.id, data: { isActive: !toggleTarget.isActive } });
      toast.success(toggleTarget.isActive ? 'Customer marked inactive' : 'Customer reactivated');
      setToggleTarget(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not change the customer status'));
    }
  };

  const hasFilters = !!(search || statusFilter || creditFilter);

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setCreditFilter('');
    setPage(1);
  };

  const stats = [
    {
      label: 'Total Customers',
      value: formatNumber(summary?.total ?? 0),
      hint: `${formatNumber(summary?.inactive ?? 0)} inactive`,
      icon: UsersIcon,
      tone: 'text-indigo-600 bg-indigo-50',
    },
    {
      label: 'Active',
      value: formatNumber(summary?.active ?? 0),
      hint: 'Can be put on new orders',
      icon: CheckCircleIcon,
      tone: 'text-green-600 bg-green-50',
    },
    {
      label: 'Outstanding',
      value: formatMoney(summary?.totalOutstanding ?? 0),
      hint: `of ${formatMoney(summary?.totalCreditLimit ?? 0)} credit extended`,
      icon: BanknotesIcon,
      tone: 'text-teal-600 bg-teal-50',
    },
    {
      label: 'Over Credit Limit',
      value: formatNumber(summary?.overLimit ?? 0),
      hint: summary?.overLimit ? 'Owe more than their limit' : 'Everyone is within their limit',
      icon: ExclamationTriangleIcon,
      tone: summary?.overLimit ? 'text-red-600 bg-red-50' : 'text-gray-500 bg-gray-100',
    },
  ];

  const selectClass =
    'h-10 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500';
  const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
  const iconButtonClass = 'p-1.5 rounded disabled:opacity-50';

  const renderStatus = (customer: { isActive: boolean }) => (
    <span
      className={`${badgeClass} ${
        customer.isActive ? 'text-green-700 bg-green-50 ring-green-600/20' : 'text-gray-700 bg-gray-50 ring-gray-500/20'
      }`}
    >
      {customer.isActive ? 'Active' : 'Inactive'}
    </span>
  );

  const renderOutstanding = (customer: CustomerListItem) => {
    const percent = getUtilisation(customer);
    if (percent === null) {
      return (
        <>
          <div className="font-medium text-gray-900 tabular-nums">{formatMoney(customer.currentOutstanding)}</div>
          <div className="text-xs text-gray-500">No credit limit</div>
        </>
      );
    }
    const tone = getUtilisationTone(percent);
    return (
      <>
        <div className="font-medium text-gray-900 tabular-nums">{formatMoney(customer.currentOutstanding)}</div>
        <div className="mt-1 h-1.5 w-28 overflow-hidden rounded-full bg-gray-100">
          <div className={`h-full rounded-full ${tone.bar}`} style={{ width: `${Math.min(100, percent)}%` }} />
        </div>
        <div className={`mt-0.5 text-xs ${tone.text}`}>
          {formatNumber(percent, 0)}% of limit{percent > 100 ? ' — over limit' : ''}
        </div>
      </>
    );
  };

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error loading customers</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Customers</h1>
          <p className="mt-1 text-sm text-gray-600">Manage customer details, GST registration and credit limits</p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Customer
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="flex items-center gap-4 rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200"
          >
            <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${stat.tone}`}>
              <stat.icon className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-gray-500">{stat.label}</p>
              <p className="truncate text-xl font-semibold text-gray-900">{isSummaryLoading ? '—' : stat.value}</p>
              <p className="truncate text-xs text-gray-500">{isSummaryLoading ? ' ' : stat.hint}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        {/* Search & filters */}
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, code, email, phone, contact, city or GST number..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="block h-10 w-full rounded-md border border-gray-300 bg-white pl-10 pr-3 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <select
              aria-label="Filter by status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as 'active' | 'inactive' | '');
                setPage(1);
              }}
              className={selectClass}
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <select
              aria-label="Filter by credit"
              value={creditFilter}
              onChange={(e) => {
                setCreditFilter(e.target.value as 'over_limit' | '');
                setPage(1);
              }}
              className={selectClass}
            >
              <option value="">Any credit position</option>
              <option value="over_limit">Over credit limit</option>
            </select>
            <select
              aria-label="Sort customers"
              value={sort}
              onChange={(e) => {
                setSort(e.target.value);
                setPage(1);
              }}
              className={selectClass}
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex h-10 items-center rounded-md px-2 text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                <XMarkIcon className="h-4 w-4 mr-1" />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Customers table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={thClass}>Customer</th>
                <th className={thClass}>Contact</th>
                <th className={thClass}>Location</th>
                <th className={thClass}>GST Number</th>
                <th className={`${thClass} text-right`}>Credit Limit</th>
                <th className={thClass}>Outstanding</th>
                <th className={thClass}>Status</th>
                <th className={`${thClass} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody className={`divide-y divide-gray-100 bg-white ${isFetching && !isLoading ? 'opacity-60' : ''}`}>
              {isLoading ? (
                <tr>
                  <td className="px-4 py-12 text-center text-sm text-gray-500" colSpan={8}>
                    Loading customers...
                  </td>
                </tr>
              ) : customers.length === 0 ? (
                <tr>
                  <td className="px-4 py-12 text-center" colSpan={8}>
                    <UsersIcon className="mx-auto h-10 w-10 text-gray-300" />
                    <p className="mt-2 text-sm font-medium text-gray-900">No customers found</p>
                    <p className="mt-1 text-sm text-gray-500">
                      {hasFilters
                        ? 'Try changing or clearing the search and filters.'
                        : 'Add a customer to start creating quotes and sales orders.'}
                    </p>
                  </td>
                </tr>
              ) : (
                customers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium text-gray-900">{customer.name}</div>
                      <div>
                        <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">
                          {customer.code}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="text-gray-900">{customer.contactPerson}</div>
                      <div className="text-xs text-gray-500">{customer.contactPhone}</div>
                      <div className="text-xs text-gray-500">{customer.email}</div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                      <div>{customer.city}</div>
                      <div className="text-xs text-gray-500">{customer.state}</div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm">
                      {customer.gstNumber ? (
                        <span className="font-mono text-xs text-gray-700">{customer.gstNumber}</span>
                      ) : (
                        <span className="text-xs text-gray-500">Unregistered</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm">
                      <div className="font-medium text-gray-900 tabular-nums">
                        {customer.creditLimit > 0 ? formatMoney(customer.creditLimit) : '—'}
                      </div>
                      <div className="text-xs text-gray-500">{customer.paymentTerms} days</div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm">{renderOutstanding(customer)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm">{renderStatus(customer)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-1">
                        <button
                          onClick={() => setToggleTarget(customer)}
                          className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-semibold ${
                            customer.isActive
                              ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              : 'bg-green-50 text-green-700 hover:bg-green-100'
                          }`}
                        >
                          {customer.isActive ? (
                            <PauseIcon className="h-3.5 w-3.5 mr-1" />
                          ) : (
                            <PlayIcon className="h-3.5 w-3.5 mr-1" />
                          )}
                          {customer.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => openView(customer)}
                          className={`${iconButtonClass} text-gray-600 hover:bg-gray-100 hover:text-gray-900`}
                          title="View Details"
                          aria-label="View Details"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openEdit(customer)}
                          className={`${iconButtonClass} text-indigo-600 hover:bg-indigo-50 hover:text-indigo-900`}
                          title="Edit Customer"
                          aria-label="Edit Customer"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(customer)}
                          className={`${iconButtonClass} text-red-600 hover:bg-red-50 hover:text-red-900`}
                          title="Delete Customer"
                          aria-label="Delete Customer"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!isLoading && total > 0 && (
          <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 lg:flex-row lg:items-center lg:gap-6">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500">
              <span>
                Showing {formatNumber((page - 1) * pageSize + 1)}–
                {formatNumber(Math.min(page * pageSize, total))} of {formatNumber(total)} customers
              </span>
              <label className="flex items-center gap-2">
                Rows per page
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="h-8 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <Pagination className="lg:flex-1" currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </div>

      {/* Create or edit */}
      <Modal
        isOpen={isFormOpen}
        onClose={() => !isSaving && setIsFormOpen(false)}
        title={editingCustomer ? 'Edit Customer' : 'Add Customer'}
        description={
          editingCustomer
            ? `${editingCustomer.name} · ${editingCustomer.code}`
            : 'Customers can be put on quotes, sales orders and invoices once they are saved.'
        }
        size="2xl"
        className="overflow-visible"
      >
        <CustomerForm
          key={editingCustomer?.id || 'new'}
          customer={editingCustomer}
          onSubmit={handleSubmit}
          onCancel={() => setIsFormOpen(false)}
          isSaving={isSaving}
        />
      </Modal>

      {/* Details */}
      <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title="Customer Details" size="xl">
        {viewCustomer && (
          <div className="space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p>
                  <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">
                    {viewCustomer.code}
                  </span>
                </p>
                <h4 className="mt-2 text-lg font-semibold text-gray-900">{viewCustomer.name}</h4>
                <p className="mt-1 text-sm text-gray-600">
                  {viewCustomer.city}, {viewCustomer.state} · customer since {formatDate(viewCustomer.createdAt)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {creditInfo?.isOverLimit && (
                  <span className={`${badgeClass} text-red-700 bg-red-50 ring-red-600/20`}>Over credit limit</span>
                )}
                {renderStatus(viewCustomer)}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              {[
                {
                  label: 'Credit limit',
                  value: viewCustomer.creditLimit > 0 ? formatMoney(viewCustomer.creditLimit) : 'No limit',
                  tone: 'bg-indigo-50 text-indigo-900',
                },
                {
                  label: 'Outstanding',
                  value: formatMoney(creditInfo?.currentOutstanding ?? viewCustomer.currentOutstanding),
                  tone: creditInfo?.isOverLimit ? 'bg-red-50 text-red-900' : 'bg-teal-50 text-teal-900',
                },
                {
                  label: 'Available credit',
                  value:
                    viewCustomer.creditLimit > 0
                      ? isCreditLoading
                        ? '—'
                        : formatMoney(creditInfo?.availableCredit ?? 0)
                      : 'Not limited',
                  tone: 'bg-gray-50 text-gray-900',
                },
                {
                  label: 'Overdue orders',
                  value: isCreditLoading ? '—' : formatMoney(creditInfo?.overdueAmount ?? 0),
                  tone: creditInfo?.overdueAmount ? 'bg-yellow-50 text-yellow-900' : 'bg-gray-50 text-gray-900',
                },
              ].map((item) => (
                <div key={item.label} className={`rounded-lg px-4 py-3 ${item.tone}`}>
                  <p className="text-xs font-medium uppercase tracking-wide opacity-70">{item.label}</p>
                  <p className="mt-1 text-lg font-semibold tabular-nums">{item.value}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <DetailCard title="Contact">
                <DetailItem label="Contact person">{viewCustomer.contactPerson}</DetailItem>
                <DetailItem label="Contact phone">{viewCustomer.contactPhone}</DetailItem>
                <DetailItem label="Email" className="break-all">
                  {viewCustomer.email}
                </DetailItem>
                <DetailItem label="Phone">{viewCustomer.phone}</DetailItem>
              </DetailCard>

              <DetailCard title="Tax and terms">
                <DetailItem label="GST number">
                  {viewCustomer.gstNumber ? (
                    <span className="font-mono">{viewCustomer.gstNumber}</span>
                  ) : (
                    'Unregistered'
                  )}
                </DetailItem>
                <DetailItem label="PAN number">
                  {viewCustomer.panNumber ? <span className="font-mono">{viewCustomer.panNumber}</span> : '—'}
                </DetailItem>
                <DetailItem label="Payment terms">{viewCustomer.paymentTerms} days</DetailItem>
                <DetailItem label="Last updated">{formatDate(viewCustomer.updatedAt)}</DetailItem>
              </DetailCard>
            </div>

            <DetailCard title="Billing address">
              <dd className="col-span-2 text-sm text-gray-900">
                {viewCustomer.address}
                <br />
                {viewCustomer.city}, {viewCustomer.state} {viewCustomer.pincode}
              </dd>
            </DetailCard>

            <section className="overflow-hidden rounded-lg border border-gray-200">
              <h4 className="border-b border-gray-200 bg-gray-50 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Open orders counted as outstanding
              </h4>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-white">
                    <tr>
                      <th className={thClass}>Order</th>
                      <th className={thClass}>Status</th>
                      <th className={thClass}>Order Date</th>
                      <th className={thClass}>Expected Delivery</th>
                      <th className={`${thClass} text-right`}>Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {isCreditLoading ? (
                      <tr>
                        <td className="px-4 py-6 text-center text-sm text-gray-500" colSpan={5}>
                          Loading orders...
                        </td>
                      </tr>
                    ) : !creditInfo || creditInfo.pendingOrders.length === 0 ? (
                      <tr>
                        <td className="px-4 py-6 text-center text-sm text-gray-500" colSpan={5}>
                          This customer has no open orders
                        </td>
                      </tr>
                    ) : (
                      creditInfo.pendingOrders.slice(0, 8).map((order) => (
                        <tr key={order.orderId}>
                          <td className="whitespace-nowrap px-4 py-2.5 text-sm">
                            <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">
                              {order.orderNumber}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-4 py-2.5 text-sm text-gray-700">
                            {ORDER_STATUS_LABELS[order.status] || order.status}
                          </td>
                          <td className="whitespace-nowrap px-4 py-2.5 text-sm text-gray-700">
                            {formatDate(order.orderDate)}
                          </td>
                          <td className="whitespace-nowrap px-4 py-2.5 text-sm text-gray-700">
                            {formatDate(order.expectedDeliveryDate)}
                          </td>
                          <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm font-medium text-gray-900 tabular-nums">
                            {formatMoney(order.amount)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {creditInfo && creditInfo.pendingOrders.length > 8 && (
                <p className="border-t border-gray-200 bg-gray-50 px-4 py-2 text-xs text-gray-500">
                  And {creditInfo.pendingOrders.length - 8} more open orders
                </p>
              )}
            </section>
          </div>
        )}

        <FormActions>
          <button type="button" onClick={() => setIsViewOpen(false)} className={secondaryButtonClass}>
            Close
          </button>
          {viewCustomer && (
            <button
              type="button"
              onClick={() => {
                setIsViewOpen(false);
                openEdit(viewCustomer);
              }}
              className={primaryButtonClass}
            >
              <PencilIcon className="mr-2 h-4 w-4" />
              Edit Customer
            </button>
          )}
        </FormActions>
      </Modal>

      {/* Activate / deactivate confirmation */}
      <ConfirmDialog
        isOpen={!!toggleTarget}
        onClose={() => setToggleTarget(null)}
        onConfirm={confirmToggle}
        title={toggleTarget?.isActive ? 'Deactivate Customer' : 'Activate Customer'}
        message={
          <>
            <span className="font-medium text-gray-900">{toggleTarget?.name}</span>{' '}
            {toggleTarget?.isActive
              ? 'will no longer be available on new quotes and sales orders. Existing orders and invoices are not changed.'
              : 'will be available on new quotes and sales orders again.'}
          </>
        }
        confirmLabel={toggleTarget?.isActive ? 'Deactivate' : 'Activate'}
        pendingLabel="Updating..."
        isPending={updateMutation.isPending}
        tone="primary"
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Delete Customer"
        message={
          <>
            <span className="font-medium text-gray-900">{deleteTarget?.name}</span> will be permanently deleted.
            This cannot be undone. A customer that already has orders, invoices or price lists cannot be deleted
            and should be deactivated instead.
          </>
        }
        confirmLabel="Delete"
        pendingLabel="Deleting..."
        isPending={deleteMutation.isPending}
      />
    </div>
  );
};

export default CustomerList;
